/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import helperLinks from './AppAssemblyConstants/helperLinks';
import landingPage from './AppAssemblyConstants/landingPage';
import profileComponent from './AppAssemblyConstants/profileComponent';
import services from './AppAssemblyConstants/services';
import solutions from './AppAssemblyConstants/solutions';
import * as translations from '../__localization__/AppAssemblyService';
//Hardcoded Custom Apps service
const customApps = (env) => {
  /* eslint-disable sort-keys */
  let endpoints = {
    dev: 'https://experience-qa.adobe.com/#/custom-apps',
    qa: 'https://experience-qa.adobe.com/#/custom-apps',
    stage: 'https://experience-stage.adobe.com/#/custom-apps',
    prod: 'https://experience.adobe.com/#/custom-apps'
  };

  return {
    appId: 'firefly',
    enabled: true,
    href: endpoints[env],
    longName: 'Project Firefly Apps',
    name: 'Project Firefly Apps',
    visible: false
  };
};
// Whitelist of orgs for Project Firefly
const CUSTOM_APPS_WHITELIST = [
  '6578A55456E84E247F000101@AdobeOrg',
  'C74F69D7594880280A495D09@AdobeOrg',
  '2A74564158A4D56F0A495C55@AdobeOrg',
  'BA3E5257595416580A495C6D@AdobeOrg',
  '7EF5AE375630F4CD7F000101@AdobeOrg',
  'A0813D885A0C0D750A495C77@AdobeOrg',
  '050BE2D6561772AD7F000101@AdobeOrg',
  '2B96335559A6E3AE0A49412E@AdobeOrg',
  '048F5DE85620B4D87F000101@AdobeOrg',
  '3C9419175E9D393C0A495E39@AdobeOrg',
  'E95186D65A28ABF00A495D82@AdobeOrg',
  'CB3867055C9575630A49421E@AdobeOrg'
];

const POST_PARAMS = '&response_type=code&scope=openid,avatar,read_organizations,'
+ 'additional_info.job_function,session,additional_info.projectedProductContext,'
+ 'additional_info.account_type,additional_info.roles,additional_info.user_image_url';

/* eslint-disable sort-keys, no-unused-vars */
const UNIFIED_SHELL_ENVS = {
  dev: 'https://experience-qa.adobe.com',
  qa: 'https://experience-qa.adobe.com',
  stage: 'https://experience-stage.adobe.com',
  prod: 'https://experience.adobe.com'
};
/* eslint-enable sort-keys, no-unused-vars */

export default class AppAssemblyService {
  constructor(shellConfig) {
    this.authorizedImsOrgs = this.populateOrgList(shellConfig.imsOrgs, shellConfig.orgTenantMap);
    this.environment = shellConfig.environment;
    this.locale = shellConfig.locale.replace(/-/g, '_');
    this.profile = shellConfig.profile;
    this.session = shellConfig.session;
    this.userRoles = shellConfig.userRoles;
    this.userServiceCodes = shellConfig.userServiceCodes;

    if (this.environment === 'qa' || this.environment === 'dev' && process.env.NODE_ENV !== 'test') {
      // eslint-disable-next-line no-console
      console.warn('The shellResponse from the AppAssemblyService is deprecated. ' +
      'You can find out more at https://git.corp.adobe.com/exc/unified-shell/blob/master/docs/deprecation.md.');
    }
  }

  /**
   * Gets the helper links
   * @returns {Object} loginAuthUri, loginPageUri, orgSwitcherEndpoint values
   */
  getHelperLinks() {
    const config = helperLinks[this.environment];
    const clientId = config.ims.clientId;
    const loginServiceEndpoint = `https://exc-login.${config.excDomain}/`;
    const redirectLocation = encodeURIComponent(window.location.href);

    let loginAuthUri = `${config.ims.loginEndpoint}ims/authorize/v1?client_id=`
      + `${clientId}&redirect_uri=${loginServiceEndpoint}exc-session/login?`
      + `provider=${clientId}` + `${redirectLocation ? `&redirectLocation=${redirectLocation}`
      : `''`}` + `${POST_PARAMS}`;
    let loginPageUri = `${loginServiceEndpoint}exc-content/login.html`;
    let orgSwitcherEndpoint = `${loginServiceEndpoint}exc-session/updateactiveorg?orgid={imsOrgId}`;
    return {loginAuthUri, loginPageUri, orgSwitcherEndpoint};
  }

  /**
   * Gets the landing page object with correct href
   * @returns {Object} Item includes name, href, icon, iconReact, longName
   */
  getLandingPage() {
    const {name, icon, iconReact, longName} = landingPage;
    return {href: landingPage.href[this.environment], icon, iconReact, longName, name};
  }

  /**
   * Gets correct user profile endpoints
   * @param {string} currentTenant name of current tenant
   * @returns {Object} User specific endpoints for user profile
   */
  getProfileComponent(currentTenant) {
    return {
      editProfileLink: this.setHref(profileComponent.editprofile[this.environment], currentTenant),
      signOutLink: this.setHref(profileComponent.logout[this.environment], currentTenant),
      userAvatarUrl: this.profile.avatar,
      userName: this.profile.displayName,
      userTitle: this.profile.job_function
    };
  }

  /**
   * Whether the solution is enabled for the user on the current org
   * @param {string} listType Identifies the type of list, i.e. 'services' vs 'solutions'
   * @param {Object} itemConfig The current config for an individual service or solution
   * @param {string} currentOrg Current org ID
   * @returns {boolean}
   */
  isEnabled(listType, itemConfig, currentOrg) {
    // First, check the current org's service codes
    let enabled = itemConfig.serviceCode && itemConfig.serviceCode.some(sc => {
      return this.userServiceCodes[currentOrg].includes(sc);
    });

    if (listType === 'solutions' || enabled) {
      return enabled || false;
    }

    // Then, check services for other enablement rules
    // Enabled will be set to true if "default" : "true" and serviceCode is null
    // (but not if currentTenant is null)
    if (currentOrg && itemConfig.default && !itemConfig.serviceCode) {
      enabled = itemConfig.default;
    }
    // Admin is a special case
    // Enabled is true is the user's roles include a matching role
    if (itemConfig.roles) {
      // Check the user's roles in the current org
      enabled = this.userRoles.some(role => {
        return role.organization === currentOrg && itemConfig.roles.includes(role.named_role);
      });
    }
    return enabled || false;
  }

  /**
   * Populates a standard format list of authorized orgs.
   * @param {Array} orgs List of user's ims orgs.
   * @param {Object} orgTenantMap Map of tenant IDs by org ID.
   * @returns {Array} Returns list of orgs with IDs, name, and tenant.
   */
  populateOrgList(orgs, orgTenantMap) {
    return orgs.map(org => ({
      imsOrgId: org.value,
      imsOrgName: org.label,
      tenantId: orgTenantMap[org.value]
    }));
  }

  /**
   * Creates a list of shell services and solutions objects
   * @param {string} listType Flags logic for service vs solution
   * @param {string} currentOrg Current org ID
   * @param {string} currentTenant current tenant name
   * @returns {Array} Returns list with of objects, each with name, longName, icon,
   * iconReact, description, learnMoreLink, enabled, href, and visible
   */
  populateSolutionSwitcherList(listType, currentOrg, currentTenant) {
    let list = listType === 'services' ? services : solutions;
    return list.map(itemConfig => {
      const {icon, iconReact, longName, name} = itemConfig;
      let item = {icon, iconReact, longName, name};
      // Get localized description and learnMoreLinks
      let {description, learnmorelink} = translations[this.locale][itemConfig.name];
      item.description = description;
      item.learnMoreLink = learnmorelink;
      // Check enablement
      item.enabled = this.isEnabled(listType, itemConfig, currentOrg);
      // Get and format the href
      item.href = this.setHref(itemConfig.href[this.environment], currentTenant);

      // Analytics always has a custom href
      if (item.name === 'Analytics') {
        // If user is not on Safari, use unified shell links
        // if (!window.navigator.vendor.match(/[Aa]pple/g)) {
        //   item.href = `${UNIFIED_SHELL_ENVS[this.environment]}/#/@${currentTenant}/analytics?IMS=1`;
        // }

        const contexts = this.profile.projectedProductContext
          .filter(c => c.prodCtx.serviceCode === 'dma_analytics')
          .filter(c => c.prodCtx.owningEntity === currentOrg)
          .sort(c => c.prodCtx.login_company);
        if (contexts.length) {
          let preferred = contexts.find(c => !!c.prodCtx.is_preferred);
          let company = preferred ? preferred.prodCtx.login_company : contexts[0].prodCtx.login_company;
          item.href = `${item.href}&company=${company}&current_org=${currentOrg}`;
        }
      }

      // Visible will be set to true for solutions
      if (listType === 'solutions') {
        item.visible = true;
      } else {
        // Visible will be set to true for Admin if it's enabled
        // Otherwise visible will be set to true for services when "default" : "true"
        item.visible = item.name === 'Admin' ? item.enabled : itemConfig.default;
      }
      return item;
    });
  }

  /**
   * Find and replace tenant in href strings
   * @param {string} endpoint The const endpoint for the href
   * @param {strong} currentTenant Tenant name to populate href
   * @returns {string} Returns href including the tenant string
   */
  setHref(endpoint, currentTenant) {
    return /<tenant>/g.test(endpoint) ? endpoint.replace(/<tenant>/g, currentTenant) : endpoint;
  }

  /**
   * Fetch the necessary shell values
   * @returns {Object} Returns an object of the necessary shell data
   */
  fetchShell(accessToken, expiryTime, currentOrg, currentOrgName, currentTenant) {
    let {loginAuthUri, loginPageUri, orgSwitcherEndpoint} = this.getHelperLinks();
    let services = this.populateSolutionSwitcherList('services', currentOrg, currentTenant);

    // Whitelisted orgs always get Custom Apps
    if (CUSTOM_APPS_WHITELIST.includes(currentOrg)) {
      services.push(customApps(this.environment));
    }

    return {
      authorizedImsOrgs: this.authorizedImsOrgs,
      currentActiveOrg: {
        imsOrgId: currentOrg,
        imsOrgName: currentOrgName,
        tenantId: currentTenant
      },
      expiryTime: JSON.stringify(expiryTime),
      imsUserToken: accessToken,
      landingpage: this.getLandingPage(),
      loginAuthUri,
      loginPageUri,
      orgSwitcherEndpoint,
      profileComponent: this.getProfileComponent(currentTenant),
      services,
      solutions: this.populateSolutionSwitcherList('solutions', currentOrg, currentTenant),
      status: [{enabled: !!this.session, field: 'authenticated'}]
    };
  }
}
